import { Injectable } from '@angular/core';
import { WldTermsContent } from '@interfaces';
import { DocumentSlice } from '@interfaces/models/doc-types';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { WldTermsService } from '@shared/services/document-upload/wld-terms.service';
import { DocumentsService } from '@shared/services/documents/documents.service';
import { StopPendingEffectsService } from '@src/app/core/services/stop-pending-effects.service';
import {
  acceptWldTerms,
  acceptWldTermsFailure,
  acceptWldTermsSuccess,
  closeSpinner,
  deleteDocumentsFailed,
  deleteDocumentsFromS3,
  deleteDocumentsSuccess,
  fetchDocumentsFailed,
  fetchDocumentsFromS3,
  fetchDocumentsSuccess,
  Go,
  loadWldConsentStatus,
  loadWldConsentStatusFailure,
  loadWldConsentStatusSuccess,
  loadWldTermsContent,
  loadWldTermsContentFailure,
  loadWldTermsContentSuccess,
  LogError,
  LogInfo,
  openSpinner,
  rehydrateFilesFromS3,
  setS3BucketFileNames,
  updateFilesMetaData,
  uploadDocumentsFailed,
  uploadDocumentsSuccess,
  uploadDocumentsToS3,
} from '@store/actions';
import {
  selectWldConsentChecked,
  selectWldTermsContent,
} from '@store/selectors/document-upload/document-upload.selectors';
import { selectDraftId } from '@store/selectors/draft/draft.selectors';
import {
  catchError,
  concatMap,
  filter,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  timeout,
  withLatestFrom,
} from 'rxjs';

import * as fromRootStore from '../../index';

const WLD_TERMS_REQUIRED_FIELDS: ReadonlyArray<keyof WldTermsContent> = [
  'reviewPageHeaderConsent',
  'documentShareTermHeader',
  'documentShareTermContent',
  'whatIsItHeader',
  'whatIsItContent',
  'howShouldIUseIt',
  'howShouldIUseItContent',
  'howShouldINotUseItHeader',
  'howShouldINotUseItContent',
  'howIsInformationProtHeader',
  'howIsInformationProtContent',
  'whatIsInformationUsedHeader',
  'whatIsInformationUsedContent',
  'consentAgreement',
];

export class MalformedTermsResponseError extends Error {
  constructor(message = 'Invalid WLD terms shape') {
    super(message);
    this.name = 'MalformedTermsResponseError';
  }
}

export function validateWldTermsShape(response: unknown): WldTermsContent {
  if (!response || typeof response !== 'object') {
    throw new MalformedTermsResponseError();
  }
  const r = response as Record<string, unknown>;
  for (const field of WLD_TERMS_REQUIRED_FIELDS) {
    const value = r[field];
    if (typeof value !== 'string' || value.length === 0) {
      throw new MalformedTermsResponseError(`Missing or empty field: ${field}`);
    }
  }
  return r as unknown as WldTermsContent;
}

/**
 * Maps a terms-content load error to the message persisted in the store. Kept as a
 * flat if-chain (no nested ternaries) so the cases stay readable.
 */
export function mapTermsErrorMessage(error: { name?: string; message?: string }): string {
  if (error.name === 'TimeoutError') {
    return 'Request timed out after 30 seconds';
  }
  if (error.name === 'MalformedTermsResponseError') {
    return 'malformed_response';
  }
  return error.message || 'Failed to load terms content';
}

@Injectable()
export class DocumentsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private documentsService: DocumentsService,
    private wldTermsService: WldTermsService,
    private stopPendingEffectsService: StopPendingEffectsService,
  ) {}

  uploadToS3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadDocumentsToS3),
      switchMap((action) => {
        return from(this.documentsService.uploadDocuments(action.filesMetadata)).pipe(
          switchMap((response) => {
            if (!response) {
              return [uploadDocumentsFailed()];
            }
            const s3BucketFileNames = action.filesMetadata
              .filter((file) => file.objectURL && file.s3BucketFileName)
              .map((file) => ({
                objectURL: file.objectURL,
                s3BucketFileName: file.s3BucketFileName!,
              }));
            return [
              uploadDocumentsSuccess(),
              setS3BucketFileNames({ payload: s3BucketFileNames }),
              LogInfo({
                payload: {
                  eventId: 'documents-upload-to-s3-success',
                  message: `Uploaded ${action.filesMetadata.length} file(s) to S3`,
                  code: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            ];
          }),
          catchError((e: Error) => {
            return of(
              uploadDocumentsFailed(),
              LogError({
                payload: {
                  eventId: 'error-documents-upload-failure',
                  message: e.message,
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            );
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  deleteFromS3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteDocumentsFromS3),
      withLatestFrom(this.store$.select(selectDraftId)),
      filter(([action, draftId]) => !!draftId && action.fileDescriptors.length > 0),
      concatMap(([action, draftId]) =>
        from(this.documentsService.deleteDocuments(draftId!, action.fileDescriptors)).pipe(
          // Unlike the 30s WLD content timeouts elsewhere in this file, this is a
          // best-effort background cleanup call that never surfaces to the user, and
          // the middleware's own upstream read caps at 3s — 10s frees the browser
          // connection sooner rather than holding it for the full 30s.
          timeout(10000),
          map(() => deleteDocumentsSuccess()),
          // Cleanup is best-effort: a failure must never surface to the user or block
          // their next step. The S3 lifecycle rule is the backstop.
          catchError((e: Error) =>
            of(
              deleteDocumentsFailed(),
              LogError({
                payload: {
                  eventId: 'error-documents-delete-failure',
                  message: e.message,
                  error: e,
                  draftId,
                  fileCount: action.fileDescriptors.length,
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            ),
          ),
        ),
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  fetchFromS3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fetchDocumentsFromS3),
      switchMap((action) => {
        this.store$.dispatch(openSpinner());
        return from(this.documentsService.fetchDocuments(action.fileDescriptors)).pipe(
          switchMap((restored) => {
            if (!restored) {
              return [fetchDocumentsFailed(), closeSpinner()];
            }
            const rehydratePayload = restored.map((file) => ({
              s3BucketFileName: file.fileName,
              objectURL: file.objectURL,
            }));
            const logAction = LogInfo({
              payload: {
                eventId: 'documents-fetch-from-s3-success',
                message: `Fetched file(s) from S3`,
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            });
            return [
              rehydrateFilesFromS3({ payload: rehydratePayload, slice: action.slice }),
              fetchDocumentsSuccess(),
              closeSpinner(),
              logAction,
            ];
          }),
          catchError((e: Error) => {
            return of(
              fetchDocumentsFailed(),
              closeSpinner(),
              LogError({
                payload: {
                  eventId: 'error-documents-fetch-failure',
                  message: `Failed to fetch files from S3 for slice="${action.slice || 'document'}": ${e.message}`,
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            );
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  loadWldTermsContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWldTermsContent),
      withLatestFrom(this.store$.select(selectWldTermsContent)),
      switchMap(([, cachedTermsContent]) => {
        // Short-circuit with the cached payload so the reducer clears termsLoading.
        if (cachedTermsContent) {
          return of(
            loadWldTermsContentSuccess({ payload: cachedTermsContent }),
            LogInfo({
              payload: {
                eventId: 'wld-terms-content-load-success',
                message: 'WLD terms content served from cache',
                code: LoggerCodes.GENERAL_SUCCESS,
              },
            }),
          );
        }
        return this.wldTermsService.getTermsContent().pipe(
          timeout(30000),
          map((content) => validateWldTermsShape(content)),
          switchMap((content) =>
            of(
              loadWldTermsContentSuccess({ payload: content }),
              LogInfo({
                payload: {
                  eventId: 'wld-terms-content-load-success',
                  message: 'WLD terms content loaded from service',
                  code: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            ),
          ),
          catchError((error) => {
            return of(
              loadWldTermsContentFailure({ error: mapTermsErrorMessage(error) }),
              Go({ path: ['error'] }),
              LogError({
                payload: {
                  eventId: 'error-page-route-wld-terms-content',
                  message: 'Failed to load Document Share terms content',
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            );
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  loadWldConsentStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWldConsentStatus),
      withLatestFrom(this.store$.select(selectWldConsentChecked)),
      filter(([{ force }, alreadyChecked]) => force || !alreadyChecked),
      switchMap(() => {
        return this.wldTermsService.getDocumentSharingConsent().pipe(
          timeout(30000),
          switchMap((status) =>
            of(
              loadWldConsentStatusSuccess({ payload: status }),
              LogInfo({
                payload: {
                  eventId: 'wld-consent-status-load-success',
                  message: `Loaded WLD document sharing consent status: responseStatus=${status?.responseStatus}`,
                  code: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            ),
          ),
          catchError((error) => {
            const errorMessage =
              error?.name === 'TimeoutError'
                ? 'Request timed out after 30 seconds'
                : error?.message || 'Failed to load consent status';
            return of(
              loadWldConsentStatusFailure({ error: errorMessage }),
              LogError({
                payload: {
                  eventId: 'error-wld-consent-status-load-failure',
                  message: `Failed to load WLD document sharing consent status: ${errorMessage}`,
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            );
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  acceptWldTerms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(acceptWldTerms),
      switchMap(() => {
        return this.wldTermsService.acceptDocumentSharingConsent().pipe(
          timeout(30000),
          switchMap((consent) =>
            of(
              acceptWldTermsSuccess({ payload: consent }),
              // The accept POST does not return the acceptance date, so force a GET
              // of the consent status to retrieve lastModifiedDateTime.
              loadWldConsentStatus({ force: true }),
              LogInfo({
                payload: {
                  eventId: 'wld-consent-accept-success',
                  message: 'User accepted WLD document sharing consent',
                  code: LoggerCodes.GENERAL_SUCCESS,
                },
              }),
            ),
          ),
          catchError((error) => {
            const errorMessage =
              error?.name === 'TimeoutError'
                ? 'Request timed out after 30 seconds'
                : error?.message || 'Failed to record consent acceptance';
            return of(
              acceptWldTermsFailure({ error: errorMessage }),
              LogError({
                payload: {
                  eventId: 'error-wld-consent-accept-failure',
                  message: `Failed to record WLD consent acceptance: ${errorMessage}`,
                  code: LoggerCodes.NULL_FAILURE,
                },
              }),
            );
          }),
        );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );
}
